'use client';

import { useMemo } from 'react';

import {
    Box,
    BoxContent,
    Link,
    RadioTile,
    RadioTiles,
    Rows,
    Text,
    TextLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { filterPromotionsByTypesMarketing } from '@services/products';

import { FundingType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { PlanNuggetsList } from '@components/plan';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { hasInstallment } from '@helpers/funding';

import { MarketingType } from '@constants/plan';

import {
    CategoryBox,
    PromoHome,
    PromoPortability,
    SelectFunding,
    SkeletonPlan,
    WithOrWithoutPlan,
} from '@product-detail/components';
import { PricesDetails } from '@product-detail/helpers';

interface PlansProps {
    plans: MobilePlanType[];
    currentPlan?: MobilePlanType;
    setCurrentPlan: (plan?: MobilePlanType) => void;
    marketings: MarketingsState;
    setMarketings: (marketings: PartialMarketingsState) => MarketingsState;
    openPlanModal: () => void;
    setFunding: (funding?: FundingType) => void;
    prices: PricesDetails;
    isLoading: boolean;
    user?: UserType;
    isRenewal: boolean;
    onChangeClientBbox: (isClientBbox: boolean) => void;
    funding?: FundingType;
    withoutPlanPrice: number;
}

export function Plans({
    plans,
    currentPlan,
    setCurrentPlan,
    marketings,
    setMarketings,
    openPlanModal,
    setFunding,
    prices,
    isLoading,
    user,
    isRenewal,
    onChangeClientBbox,
    funding,
    withoutPlanPrice,
}: Readonly<PlansProps>) {
    const hasPromotions = useMemo(() => {
        if (!currentPlan || !currentPlan.details?.discounts) return false;

        const homePromos = filterPromotionsByTypesMarketing(
            currentPlan.details.discounts,
            [MarketingType.HOME],
            true,
            true,
        );

        const portabilityPromos = filterPromotionsByTypesMarketing(
            currentPlan.details.discounts,
            [MarketingType.PORTABILITY],
            true,
            true,
        );

        return homePromos.length > 0 || portabilityPromos.length > 0;
    }, [currentPlan]);

    if (plans.length === 0) return null;

    if (isLoading) return <SkeletonPlan isRenewal={isRenewal} />;

    return (
        <>
            {!isRenewal && (
                <WithOrWithoutPlan
                    plans={plans}
                    currentPlan={currentPlan}
                    setCurrentPlan={setCurrentPlan}
                    withoutPlanPrice={withoutPlanPrice}
                />
            )}
            {(!isRenewal || hasPromotions) && currentPlan !== undefined && (
                <CategoryBox
                    title={isRenewal ? 'Je configure mon offre' : 'Quel forfait vous faut-il\u00a0?'}
                    formId='offerSelect'
                >
                    {!isRenewal && (
                        <>
                            <RadioTiles className='grid-radio-list'>
                                {plans.map((plan) => (
                                    <RadioTile
                                        id={plan.gencode}
                                        key={plan.gencode}
                                        label={plan.dataEnvelope}
                                        name='planChoice'
                                        onChange={() => setCurrentPlan(plan)}
                                        checked={currentPlan?.gencode === plan.gencode}
                                        horizontal
                                    />
                                ))}
                            </RadioTiles>
                            <Box shadowless>
                                <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                                    <PlanNuggetsList plan={currentPlan} />
                                    <Text level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                                        <Link onClick={openPlanModal} role='button'>
                                            Voir plus de détails
                                        </Link>
                                    </Text>
                                </BoxContent>
                            </Box>
                        </>
                    )}
                    <Rows>
                        <PromoHome
                            currentPlan={currentPlan}
                            setMarketings={setMarketings}
                            marketings={marketings}
                            user={user}
                            isRenewal={isRenewal}
                            onChangeClientBbox={onChangeClientBbox}
                        />
                        <PromoPortability
                            currentPlan={currentPlan}
                            setMarketings={setMarketings}
                            marketings={marketings}
                            isRenewal={isRenewal}
                        />
                    </Rows>
                </CategoryBox>
            )}
            <CategoryBox
                title={
                    currentPlan?.fundings.some((f) => hasInstallment(f.type))
                        ? 'Quel paiement pour votre téléphone\u00A0?'
                        : 'Votre paiement en détail'
                }
                formId='fundingSelect'
            >
                <SelectFunding
                    currentPlan={currentPlan}
                    currentFunding={funding}
                    setCurrentFunding={setFunding}
                    isRenewal={isRenewal}
                    prices={prices}
                    withoutPlanPrice={withoutPlanPrice}
                />
            </CategoryBox>
        </>
    );
}
